import Vue from 'vue'
import VueRouter from 'vue-router'
import { IMetaTag } from '@/router/IMetaTag'
import { DEFAULT_NETWORK } from '@/store/modules/network/network'

Vue.use(VueRouter)

const suffix = ' | DComm Explorer'
const description =
    'DComm Explorer is an analytics tool that enables people to search the DComm blockchain for transactions, addresses, and other authority activities.'
const defaultMetaTags: IMetaTag[] = [
    {
        name: 'description',
        content: description,
    },
    {
        property: 'og:description',
        content: description,
    },
]

const ACTID = DEFAULT_NETWORK.actChainId
const ASTID = DEFAULT_NETWORK.astChainId
const ATHID = DEFAULT_NETWORK.athChainId

export interface ChainPath {
    [key: string]: string
}

export const chainPath: ChainPath = {
    ['ACT']: `/blockchain/${ACTID}`,
    ['AST']: `/blockchain/${ASTID}`,
    ['ATH']: `/blockchain/${ATHID}`,
}

const routes = [
    {
        path: `/`,
        name: 'Home',
        component: () =>
            import(/* webpackChunkName: "subnets" */ '../views/Home.vue'),
        meta: {
            auth: false,
            title: 'View All Activities' + suffix,
            metaTags: defaultMetaTags,
        },
        // beforeEnter() {
        //     if (window.location.href !== actChainURL) {
        //         window.location.href = actChainURL
        //     }
        // },
    },
    {
        path: `/action`,
        name: 'Action Chain',

        component: () =>
            import(
                /* webpackChunkName: "subnets" */ '../views/ActionChain.vue'
            ),
        meta: {
            auth: false,
            title: 'Action Chain' + suffix,
            metaTags: defaultMetaTags,
        },
    },
    {
        path: `/subnets`,
        name: 'Subnets',
        component: () =>
            import(/* webpackChunkName: "subnets" */ '../views/Subnets.vue'),
        meta: {
            auth: false,
            title: 'Subnets' + suffix,
            metaTags: defaultMetaTags,
        },
    },
    {
        path: `/subnet/:id`,
        name: 'Subnet',
        component: () =>
            import(/* webpackChunkName: "subnet" */ '../views/Subnet.vue'),
        meta: {
            auth: false,
            title: 'Subnet' + suffix,
            metaTags: defaultMetaTags,
        },
    },
    {
        path: `/validators`,
        name: 'Validators',
        component: () =>
            import(
                /* webpackChunkName: "validators" */ '../views/Validators.vue'
            ),
        meta: {
            auth: false,
            title: 'Validators Transparency' + suffix,
            metaTags: [
                {
                    name: 'description',
                    content:
                        'DComm Explorer for validators is the perfect analytics tool that enables transactions, addresses, and other authority transparency.',
                },
                {
                    property: 'og:description',
                    content:
                        'DComm Explorer for validators is the perfect analytics tool that enables transactions, addresses, and other authority transparency.',
                },
            ],
        },
    },
    {
        path: `/tx`,
        name: 'Transactions',
        component: () =>
            import(
                /* webpackChunkName: "transactions" */ '../views/Transactions.vue'
            ),
        meta: {
            auth: false,
            title: 'Transactions' + suffix,
            metaTags: [
                {
                    name: 'description',
                    content:
                        'View transactions on AST-Chain, ATH-Chain, and ACT-Chain with the DComm Explorer. Providing our community of individuals, developers, and investors piece of mind.',
                },
                {
                    property: 'og:description',
                    content:
                        'View transactions on AST-Chain, ATH-Chain, and ACT-Chain with the DComm Explorer. Providing our community of individuals, developers, and investors piece of mind.',
                },
            ],
        },
    },
    {
        path: `/tx/:id`,
        name: 'Transaction',
        component: () =>
            import(
                /* webpackChunkName: "transaction" */ '../views/Transaction.vue'
            ),
        meta: {
            auth: false,
            title: 'Transaction' + suffix,
            metaTags: [
                {
                    name: 'description',
                    content:
                        'View transactions on AST-Chain, ATH-Chain, and ACT-Chain with the DComm Explorer. Providing our community of individuals, developers, and investors piece of mind.',
                },
                {
                    property: 'og:description',
                    content:
                        'View transactions on AST-Chain, ATH-Chain, and ACT-Chain with the DComm Explorer. Providing our community of individuals, developers, and investors piece of mind.',
                },
            ],
        },
    },
    {
        path: `/evmtx/:id`,
        name: 'EVM Transaction',
        component: () =>
            import(
                /* webpackChunkName: "evmtransaction" */ '../views/EVMTransaction.vue'
            ),
        meta: {
            auth: false,
            title: 'Transaction' + suffix,
            metaTags: defaultMetaTags,
        },
    },
    {
        path: `/evmtx`,
        name: 'EVM Transactions',
        component: () =>
            import(
                /* webpackChunkName: "evmtransaction" */ '../views/EVMTransactions.vue'
            ),
        meta: {
            auth: false,
            title: 'Transactions' + suffix,
            metaTags: defaultMetaTags,
        },
    },
    {
        path: `/blockchainevmtx/:id`,
        name: 'Blockchain EVM Transaction',
        component: () =>
            import(
                /* webpackChunkName: "evmtransaction" */ '../views/EVMTransaction.vue'
            ),
        meta: {
            auth: false,
            title: 'Transaction' + suffix,
            metaTags: defaultMetaTags,
        },
    },
    {
        path: `/blockchainevmtx`,
        name: 'Blockchain EVM Transactions',
        component: () =>
            import(
                /* webpackChunkName: "evmtransaction" */ '../views/EVMTransactions.vue'
            ),
        meta: {
            auth: false,
            title: 'Transactions' + suffix,
            metaTags: defaultMetaTags,
        },
    },
    {
        path: `/evmblock`,
        name: 'EVM Blocks',
        component: () =>
            import(/* webpackChunkName: "evmblock" */ '../views/EVMBlocks.vue'),
        meta: {
            auth: false,
            title: 'Blocks' + suffix,
            metaTags: defaultMetaTags,
        },
    },
    {
        path: `/evmblock/:id`,
        name: 'EVM Block',
        component: () =>
            import(/* webpackChunkName: "evmblock" */ '../views/EVMBlock.vue'),
        meta: {
            auth: false,
            title: 'Block' + suffix,
            metaTags: defaultMetaTags,
        },
    },
    {
        path: `/blockchainevmblock`,
        name: 'Blockchain EVM Blocks',
        component: () =>
            import(/* webpackChunkName: "evmblock" */ '../views/EVMBlocks.vue'),
        meta: {
            auth: false,
            title: 'Blocks' + suffix,
            metaTags: defaultMetaTags,
        },
    },
    {
        path: `/blockchainevmblock/:id`,
        name: 'Blockchain EVM Block',
        component: () =>
            import(/* webpackChunkName: "evmblock" */ '../views/EVMBlock.vue'),
        meta: {
            auth: false,
            title: 'Block' + suffix,
            metaTags: defaultMetaTags,
        },
    },
    {
        path: `/blockchainevmaddress/:id`,
        name: 'Blockchain EVM Address',
        component: () =>
            import(
                /* webpackChunkName: "evmaddress" */ '../views/EVMAddress.vue'
            ),
        meta: {
            auth: false,
            title: 'Address' + suffix,
            metaTags: defaultMetaTags,
        },
    },
    {
        path: `/evmaddress/:id`,
        name: 'EVM Address',
        component: () =>
            import(
                /* webpackChunkName: "evmaddress" */ '../views/EVMAddress.vue'
            ),
        meta: {
            auth: false,
            title: 'Address' + suffix,
            metaTags: defaultMetaTags,
        },
    },
    {
        path: `/blockchain`,
        name: 'Blockchains',
        component: () =>
            import(
                /* webpackChunkName: "blockchains" */ '../views/Blockchains.vue'
            ),
        meta: {
            auth: false,
            title: 'Blockchains' + suffix,
            metaTags: [
                {
                    name: 'description',
                    content:
                        'Blockchain transparency with the DComm Explorer enables people to search for transactions, addresses, and other authority activities.',
                },
                {
                    property: 'og:description',
                    content:
                        'Blockchain transparency with the DComm Explorer enables people to search for transactions, addresses, and other authority activities.',
                },
            ],
        },
    },
    {
        path: `/blockchain/:id`,
        name: 'Blockchain',
        component: () =>
            import(
                /* webpackChunkName: "blockchains" */ '../views/Blockchain.vue'
            ),
        meta: {
            auth: false,
            title: 'Blockchain' + suffix,
            metaTags: [
                {
                    name: 'description',
                    content:
                        'Blockchain transparency with the DComm Explorer enables people to search for transactions, addresses, and other authority activities.',
                },
                {
                    property: 'og:description',
                    content:
                        'Blockchain transparency with the DComm Explorer enables people to search for transactions, addresses, and other authority activities.',
                },
            ],
        },
    },
    {
        path: `/assets`,
        name: 'Assets',
        component: () =>
            import(/* webpackChunkName: "assets" */ '../views/Assets.vue'),
        meta: {
            auth: false,
            title: 'Assets' + suffix,
            metaTags: defaultMetaTags,
        },
    },
    // {
    //     path: '/layout',
    //     name: 'Layout',
    //     component: () =>
    //         import(
    //             /* webpackChunkName: "Layout" */ '../components/Home/Layout.vue'
    //         ),
    // },
    {
        path: `/asset/:id`,
        name: 'Asset',
        component: () =>
            import(/* webpackChunkName: "asset" */ '../views/Asset.vue'),
        meta: {
            auth: false,
            title: 'Asset' + suffix,
            metaTags: defaultMetaTags,
        },
    },
    {
        path: `/addresses`,
        name: 'Addresses',
        component: () =>
            import(
                /* webpackChunkName: "addresses" */ '../views/Addresses.vue'
            ),
        meta: {
            auth: false,
            title: 'Addresses' + suffix,
            metaTags: defaultMetaTags,
        },
    },
    {
        path: `/address/:address`,
        name: 'Address',
        component: () =>
            import(/* webpackChunkName: "address" */ '../views/Address.vue'),
        meta: {
            auth: false,
            title: 'Address' + suffix,
            metaTags: defaultMetaTags,
        },
    },
    {
        path: `/search`,
        name: 'Search',
        component: () =>
            import(/* webpackChunkName: "search" */ '../views/Search.vue'),
        meta: {
            auth: false,
            title: 'Search' + suffix,
            metaTags: defaultMetaTags,
        },
    },
    {
        path: `/resources`,
        name: 'Resources',
        component: () =>
            import(
                /* webpackChunkName: "resources" */ '../views/Resources.vue'
            ),
        meta: {
            auth: false,
            title: 'Resources' + suffix,
            metaTags: defaultMetaTags,
        },
    },
    {
        path: `/comingsoon`,
        name: 'Coming Soon',
        component: () =>
            import(
                /* webpackChunkName: "resources" */ '../views/ComingSoon.vue'
            ),
        meta: {
            auth: false,
            title: 'Coming Soon' + suffix,
            metaTags: defaultMetaTags,
        },
    },
    {
        path: `/:pathMatch(.*)*`,
        name: '404',
        component: () =>
            import(
                /* webpackChunkName: "resources" */ '../views/error/404.vue'
            ),
        meta: {
            auth: false,
            title: 'Not Found' + suffix,
            metaTags: defaultMetaTags,
        },
    },
]

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    // Scroll to top on route change
    scrollBehavior() {
        return { x: 0, y: 0 }
    },
    routes,
})
export default router
